/**
 * Loan Match Dialog Component - V3 SIMPLIFIED
 * 
 * Per accounting standards, loan payments are a simple:
 *   DR Loan GL (full amount), CR Bank GL
 * No principal/interest split. Interest accrual is handled separately.
 */

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { PeriodLockWarningDialog } from './PeriodLockWarningDialog';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import {
  Loader2, Landmark, ArrowUpRight, Sparkles,
  Percent, CreditCard, CheckCircle2, AlertCircle,
  XCircle, DollarSign, FileText
} from 'lucide-react';

const LOAN_TYPE_LABELS = {
  bank_loan: 'Bank Loan', mortgage: 'Mortgage', vehicle_loan: 'Vehicle Loan',
  equipment_loan: 'Equipment Loan', director_loan: 'Director Loan',
  shareholder_loan: 'Shareholder Loan', working_capital: 'Working Capital',
  overdraft: 'Overdraft', personal_loan: 'Personal Loan',
  term_loan: 'Term Loan', other: 'Other'
};

export const LoanMatchDialog = ({
  open, onOpenChange, bankTransaction, formatAmount,
  onMatchComplete, bankCurrency, baseCurrency
}) => {
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [periodLockDialog, setPeriodLockDialog] = useState({ open: false });

  const amount = useMemo(() => Math.abs(bankTransaction?.amount || 0), [bankTransaction]);
  const selectedLoan = useMemo(() => 
    matchData?.loans?.find(l => l.id === selectedLoanId),
    [matchData, selectedLoanId]
  );

  useEffect(() => {
    if (open && bankTransaction?.id) {
      fetchPotentialMatches();
    } else {
      setMatchData(null);
      setSelectedLoanId(null);
    }
  }, [open, bankTransaction?.id]);

  const fetchPotentialMatches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/bank-feeds/loan-matches/${bankTransaction.id}`);
      setMatchData(response.data);
      if (response.data?.best_match?.confidence_score >= 70) {
        setSelectedLoanId(response.data.best_match.loan_id);
      }
    } catch (error) {
      console.error('Error fetching loan matches:', error);
      toast.error('Failed to load potential loan matches');
    } finally {
      setLoading(false);
    }
  };

  const handleMatchToLoan = async () => {
    if (!selectedLoanId) {
      toast.error('Please select a loan to match');
      return;
    }
    setProcessing(true);
    
    const submitLoanMatch = async (extraParams = {}) => {
      try {
        const formData = new FormData();
        formData.append('loan_id', selectedLoanId);
        formData.append('payment_type', 'full_payment');
        formData.append('not_a_loan_payment', 'false');
        if (extraParams.allow_locked_period) formData.append('allow_locked_period', 'true');
        if (extraParams.override_date) formData.append('override_date', extraParams.override_date);

        await axios.post(
          `${API}/bank-feeds/review/${bankTransaction.id}/loan-match`,
          formData);
        toast.success('Transaction matched to loan successfully');
        onMatchComplete?.();
        onOpenChange(false);
      } catch (error) {
        console.error('Error matching to loan:', error);
        toast.error(error.response?.data?.detail || 'Failed to match transaction');
      } finally {
        setProcessing(false);
      }
    };
    
    const txDate = bankTransaction?.date;
    if (txDate) {
      try {
        const res = await axios.get(`${API}/accounting/periods/check-date/${txDate}`);
        if (res.data && !res.data.allowed) {
          setProcessing(false);
          setPeriodLockDialog({
            open: true,
            transactionDate: txDate,
            lockedPeriod: txDate.substring(0, 7),
            onPostToOriginalDate: () => submitLoanMatch({ allow_locked_period: true }),
            onPostToToday: () => submitLoanMatch({ override_date: new Date().toISOString().split('T')[0] }),
          });
          return;
        }
      } catch { /* proceed */ }
    }
    await submitLoanMatch();
  };

  const handleNotALoanPayment = async () => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('loan_id', '');
      formData.append('not_a_loan_payment', 'true');
      await axios.post(
        `${API}/bank-feeds/review/${bankTransaction.id}/loan-match`,
        formData);
      toast.success('Transaction marked as non-loan payment');
      onMatchComplete?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update transaction');
    } finally {
      setProcessing(false);
    }
  };

  const hasLoans = matchData?.loans?.length > 0;
  const newBalance = selectedLoan ? Math.max(0, (selectedLoan.remaining_balance || 0) - amount) : 0;

  return (
    <>
    <PeriodLockWarningDialog
      open={periodLockDialog.open}
      onClose={() => setPeriodLockDialog({ open: false })}
      transactionDate={periodLockDialog.transactionDate}
      lockedPeriod={periodLockDialog.lockedPeriod}
      onPostToOriginalDate={periodLockDialog.onPostToOriginalDate}
      onPostToToday={periodLockDialog.onPostToToday}
    />
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-blue-600" />
            Match to Loan Payment
          </DialogTitle>
          <DialogDescription>
            Select a loan to apply this payment against. The full amount will reduce the loan balance.
          </DialogDescription>
        </DialogHeader>

        {bankTransaction && (
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                  <ArrowUpRight className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">{bankTransaction.description?.slice(0, 50)}...</p>
                  <p className="text-xs text-muted-foreground">{bankTransaction.date}</p>
                </div>
              </div>
              <span className="text-lg font-semibold text-red-600">
                -{formatAmount?.(amount) || amount.toLocaleString()}
              </span>
            </div>
          </div>
        )}

        {bankCurrency && baseCurrency && bankCurrency !== baseCurrency && bankTransaction && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="fx-loan-conversion-notice">
            <div className="flex items-center gap-2 text-amber-800 text-sm">
              <DollarSign className="w-4 h-4 shrink-0" />
              <span className="font-medium">Foreign Currency Payment ({bankCurrency})</span>
            </div>
            <p className="text-xs text-amber-700 mt-1">
              Journal entries will be converted to {baseCurrency} at the current exchange rate.
            </p>
          </div>
        )}

        <Separator />

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-muted-foreground">Loading loans...</span>
          </div>
        )}

        {!loading && matchData && (
          <div className="flex-1 overflow-hidden space-y-4">
            {hasLoans ? (
              <>
                {matchData.best_match && (
                  <div className="mb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-medium">AI Suggestion</span>
                      <Badge variant="outline" className={
                        matchData.best_match.confidence_score >= 80 ? 'bg-green-50 text-green-700' :
                        matchData.best_match.confidence_score >= 60 ? 'bg-amber-50 text-amber-700' :
                        'bg-gray-50 text-gray-700'
                      }>
                        {matchData.best_match.confidence_score}% match
                      </Badge>
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-sm font-medium mb-2 block">Select Loan</Label>
                  <ScrollArea className="h-[200px] pr-4">
                    <RadioGroup 
                      value={selectedLoanId || ''} 
                      onValueChange={setSelectedLoanId}
                      className="space-y-2"
                    >
                      {matchData.loans?.map((loan) => (
                        <div
                          key={loan.id}
                          className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedLoanId === loan.id 
                              ? 'ring-2 ring-blue-500 bg-blue-50/50' 
                              : 'hover:bg-muted/50'
                          }`}
                          onClick={() => setSelectedLoanId(loan.id)}
                          data-testid={`loan-option-${loan.id}`}
                        >
                          <RadioGroupItem value={loan.id} id={loan.id} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <CreditCard className="w-4 h-4 text-blue-600" />
                              <span className="font-medium text-sm">{loan.loan_name}</span>
                              <Badge variant="outline" className="text-xs">
                                {LOAN_TYPE_LABELS[loan.loan_type] || loan.loan_type}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                              <span>Balance: {formatAmount?.(loan.remaining_balance) || loan.remaining_balance?.toLocaleString()}</span>
                              {loan.interest_rate > 0 && (
                                <span className="flex items-center gap-1">
                                  <Percent className="w-3 h-3" />
                                  {loan.interest_rate}% p.a.
                                </span>
                              )}
                            </div>
                          </div>
                          {selectedLoanId === loan.id && (
                            <CheckCircle2 className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                      ))}
                    </RadioGroup>
                  </ScrollArea>
                </div>

                {selectedLoanId && selectedLoan && (
                  <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-700">Payment Summary</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Payment Amount:</span>
                        <span className="font-medium">{formatAmount?.(amount) || amount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Current Balance:</span>
                        <span className="font-medium">{formatAmount?.(selectedLoan.remaining_balance) || selectedLoan.remaining_balance?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t font-semibold">
                        <span>New Balance:</span>
                        <span className={newBalance === 0 ? 'text-green-600' : ''}>
                          {formatAmount?.(newBalance) || newBalance.toLocaleString()}
                          {newBalance === 0 && ' (Paid Off)'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-medium mb-1">No Active Loans Found</h3>
                <p className="text-sm text-muted-foreground">
                  There are no active loans in the system to match against.
                </p>
              </div>
            )}
          </div>
        )}

        <Separator />

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button
            variant="outline"
            onClick={handleNotALoanPayment}
            disabled={processing}
            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
            data-testid="not-loan-btn"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Not a Loan Payment
          </Button>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>
              Cancel
            </Button>
            <Button
              onClick={handleMatchToLoan}
              disabled={processing || !selectedLoanId}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="match-loan-btn"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Match to Loan
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default LoanMatchDialog;
